import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusDot } from "@/components/brand/status-dot";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type Credential = { id: string; tenantSlug: string; label: string; createdAt: string; revokedAt: string | null };
type Tenant = { slug: string; name: string };

async function call<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const r = await fetch(path, {
    ...init,
    headers: init?.json !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: init?.json !== undefined ? JSON.stringify(init.json) : undefined,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error ?? `HTTP ${r.status}`);
  return data as T;
}

/**
 * Operator page: the per-tenant credentials companyx-tenant deployments use to
 * read their catalog at /api/catalog.
 */
export function Credentials() {
  const [creds, setCreds] = useState<Credential[]>();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenant, setTenant] = useState("");
  const [label, setLabel] = useState("");
  const [issued, setIssued] = useState<{ tenant: string; token: string } | null>(null);
  const [error, setError] = useState<string>();

  const load = () => call<{ credentials: Credential[] }>("/api/credentials").then((r) => setCreds(r.credentials), (e) => setError(e.message));
  useEffect(() => {
    void load();
    call<{ tenants: Tenant[] }>("/api/builder/tenants").then((r) => setTenants(r.tenants), () => {});
  }, []);

  return (
    <div className="grid gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tenant access</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Each tenant deployment reads its marketplace catalog from <span className="font-mono">/api/catalog</span> with its own credential. A credential only ever sees employees assigned to its tenant.
        </p>
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {issued && (
        <Alert variant="success">
          <AlertTitle>Credential for {issued.tenant} — copy it now</AlertTitle>
          <AlertDescription>
            <p>It is shown only once. Store it as the tenant's catalog credential.</p>
            <code className="mt-2 block break-all rounded bg-muted p-2 font-mono text-xs text-foreground">{issued.token}</code>
          </AlertDescription>
        </Alert>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Issue a credential</CardTitle>
          <CardDescription>Tenants come from the control plane.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(undefined);
              try {
                const r = await call<{ token: string; credential: Credential }>("/api/credentials", { method: "POST", json: { tenantSlug: tenant, label } });
                setIssued({ tenant, token: r.token });
                setLabel("");
                await load();
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          >
            <div className="grid gap-1.5">
              <Label htmlFor="cred-tenant">Tenant</Label>
              <Select value={tenant} onValueChange={setTenant}>
                <SelectTrigger id="cred-tenant" className="w-56">
                  <SelectValue placeholder="Choose a tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name} ({t.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="cred-label">Label</Label>
              <Input id="cred-label" value={label} placeholder="production" onChange={(e) => setLabel(e.target.value)} />
            </div>
            <Button type="submit" disabled={!tenant}>
              Issue
            </Button>
          </form>
        </CardContent>
      </Card>
      {creds && creds.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {creds.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono">{c.tenantSlug}</TableCell>
                <TableCell>{c.label || "—"}</TableCell>
                <TableCell>{c.revokedAt ? <StatusDot tone="neutral" label="Revoked" /> : <StatusDot tone="success" label="Active" />}</TableCell>
                <TableCell className="text-right">
                  {!c.revokedAt && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="xs" variant="ghost">
                          Revoke
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Revoke this credential?</AlertDialogTitle>
                          <AlertDialogDescription>{c.tenantSlug} will stop seeing its catalog until it is given a new credential.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction variant="destructive" onClick={() => void call(`/api/credentials/${c.id}`, { method: "DELETE" }).then(load)}>
                            Revoke
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
